// BankAuction.js
import React, { useContext, useState, useEffect, useRef } from 'react';
import MyLocationIcon from "@mui/icons-material/MyLocation";
import InputAdornment from "@mui/material/InputAdornment";
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
  Stack,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FormsBottomNavbar from './FormsBottomNavbar';
import SearchBar from './PostBack';
import { AuthContext } from '../../AuthContext/AuthContext';
import { BASE_URL } from '../../Api/ApiUrls';
import { checkBankAuctionUploadLimit } from "./checkBankAuctionUploadLimit";
import SubscriptionModal from "./SubscriptionModal";
import GuestLoginModal from '../../GuestLoginModal';
import { CircularProgress } from '@mui/material';
import SuccessAlert from '../../../Components/Users/Alertpage/SuccessAlert/SuccessAlert';
import FailureAlert from '../../../Components/Users/Alertpage/FailureAlert/FailureAlert';
import FullScreenLoader from '../../FullScreenLoader';
import { GOOGLEMAPS_API_KEY } from '../../Api/ApiUrls';
import {
  GoogleMap,
  useLoadScript,
  Autocomplete
} from "@react-google-maps/api";


const FilterContainer = styled(Paper)(({ theme }) => ({
  padding: '16px',
  margin: '8px',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  backgroundColor: '#fff',
  minHeight: "75vh",
  marginBottom: "80px"
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '16px',
  marginBottom: '12px',
  color: '#333'
}));

const containerStyle = {
  width: "100%",
  height: "300px",
  borderRadius: "12px",
  overflow: "hidden",
};

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#c47f4eff',
  color: 'white',
  fontWeight: 'bold',
  borderRadius: '8px',
  padding: '8px 16px',
  '&:hover': {
    backgroundColor: '#b38454ff',
  },
}));

const FileItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  margin: '4px 0',
  backgroundColor: '#f8f9fa',
  borderRadius: '6px',
  border: '1px solid #e9ecef',
}));

const FileInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
}));

const centerDefault = { lat: 17.3850, lng: 78.4867 };

const GOOGLE_MAPS_API_KEY = GOOGLEMAPS_API_KEY;

// ✅ Fields that only accept digits + optional single decimal point
const NUMERIC_ONLY_FIELDS = ['area', 'reservePrice', 'emdAmount', 'bidIncrement'];

const BankAuction = () => {
  const { userId, isGuest } = useContext(AuthContext);
  const navigate = useNavigate();
  const [uploadStatus, setUploadStatus] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    const checkSubscription = async () => {
      const result = await checkBankAuctionUploadLimit(userId);
      setUploadStatus(result);
      if (!result.canUpload) {
        setShowStatusModal(true);
      }
    };
    if (userId) checkSubscription();
  }, [userId]);

  useEffect(() => {
    if (isGuest) {
      setShowGuestModal(true);
    }
  }, [isGuest]);

  const [formData, setFormData] = useState({
    auctionId: '',
    bankName: '',
    propertyType: '',
    actionType: '',
    location: '',
    lat: '',
    long: '',
    cityTown: '',
    areaTown: '',
    area: '',
    possession: '',
    reservePrice: '',
    possessionStatus: '',
    emdAmount: '',
    bidIncrement: '',
    emdSubmission: '',
    auctionStartDate: '',
    auctionEndDate: '',
    bankContact: '',
    downloadAuctionFile: '',
    description: ''
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const autocompleteServiceRef = useRef(null);
  const [location, setLocation] = useState(centerDefault);
  const [address, setAddress] = useState("");
  const [usingCurrentLocation, setUsingCurrentLocation] = useState(false);
  const [locationConfirmed, setLocationConfirmed] = useState(false);

  useEffect(() => {
    const initGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setIsGoogleLoaded(true);
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        return true;
      }
      return false;
    };

    if (initGoogleMaps()) return;

    if (!window.google) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLEMAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (initGoogleMaps()) {
          console.log("✅ Google Maps API loaded successfully");
        }
      };
      script.onerror = () => {
        console.error("❌ Failed to load Google Maps API");
      };
      document.head.appendChild(script);
    }

    return () => {
      autocompleteServiceRef.current = null;
    };
  }, []);

  // ✅ Central change handler with numeric validation
  const handleFormDataChange = (field, value) => {
    if (NUMERIC_ONLY_FIELDS.includes(field)) {
      // Allow empty string or digits with optional single decimal point
      if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const fetchPlaces = (input) => {
    if (!input || input.length < 3 || !isGoogleLoaded || !autocompleteServiceRef.current) {
      setLocationOptions([]);
      return;
    }
    autocompleteServiceRef.current.getPlacePredictions(
      {
        input,
        types: ["geocode", "establishment"],
        componentRestrictions: { country: "in" }
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setLocationOptions(predictions.map((p) => p.description));
        } else {
          setLocationOptions([]);
        }
      }
    );
  };

  const debouncedFetchPlaces = useRef(null);

  useEffect(() => {
    debouncedFetchPlaces.current = debounce(fetchPlaces, 300);
  }, [isGoogleLoaded]);

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setUsingCurrentLocation(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setLocation(pos);
          try {
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${pos.lat},${pos.lng}&key=${GOOGLE_MAPS_API_KEY}`
            );
            const data = await response.json();
            if (data.status == 'OK' && data.results.length > 0) {
              setAddress(data.results[0].formatted_address);
            } else {
              setAddress(`Lat: ${pos.lat.toFixed(6)}, Lng: ${pos.lng.toFixed(6)}`);
            }
          } catch (error) {
            console.error('Google Maps API error:', error);
            setAddress(`Lat: ${pos.lat.toFixed(6)}, Lng: ${pos.lng.toFixed(6)}`);
          }
        },
        (error) => {
          console.error("Error getting current location:", error);
          alert("Error getting current location. Please enable location services.");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.geometry) {
        setUsingCurrentLocation(false);
        const newLoc = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setLocation(newLoc);
        setAddress(place.formatted_address);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.bankName.trim()) newErrors.bankName = "Bank name is required";
    if (!formData.propertyType) newErrors.propertyType = "Please select a property type";
    if (!address || address.trim() === '') newErrors.location = "Please select a location";
    if (!formData.reservePrice) newErrors.reservePrice = "Reserve price is required";
    if (!formData.emdAmount) newErrors.emdAmount = "EMD amount is required";
    if (!formData.auctionStartDate) newErrors.auctionStartDate = "Auction start date is required";
    if (!formData.auctionEndDate) newErrors.auctionEndDate = "Auction end date is required";
      if (formData.auctionStartDate && formData.auctionEndDate) {
    const start = new Date(formData.auctionStartDate);
    const end = new Date(formData.auctionEndDate);
    if (end <= start) {
      newErrors.auctionEndDate = "End date must be after start date";
    }
  }
    if (!formData.bankContact.trim()) newErrors.bankContact = "Bank contact is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (isGuest) {
      setShowGuestModal(true);
      return;
    }

    if (!validateForm()) return;

    setLoading(true);

    try {
      const check = await checkBankAuctionUploadLimit(userId);
      if (!check.canUpload) {
        setUploadStatus(check);
        setShowStatusModal(true);
        return;
      }

      const formPayload = new FormData();
      formPayload.append("user_id", userId);
      formPayload.append("bank_name", formData.bankName);
      formPayload.append("property_type", formData.propertyType);
      formPayload.append("action_type", formData.actionType);
      formPayload.append("location", address || "");
      formPayload.append("lat", location?.lat || "");
      formPayload.append("long", location?.lng || "");
      formPayload.append("city_town", formData.cityTown);
      formPayload.append("area_town", formData.areaTown);
      formPayload.append("area", formData.area);
      formPayload.append("units", "");
      formPayload.append("possession", formData.possession);
      formPayload.append("reserve_price", formData.reservePrice);
      formPayload.append("possession_status", formData.possessionStatus);
      formPayload.append("emd_amount", formData.emdAmount);
      formPayload.append("bid_increment", formData.bidIncrement);
      formPayload.append("emd_submission", formData.emdSubmission);
      formPayload.append("auction_start_datetime", formData.auctionStartDate);
      formPayload.append("auction_end_datetime", formData.auctionEndDate);
      formPayload.append("bank_contact_details", formData.bankContact);
      formPayload.append("description", formData.description);
      formPayload.append("admin_status", "Pending");

      selectedFiles.forEach((file) => {
        formPayload.append("new_documents", file);
      });

      const response = await fetch(`${BASE_URL}/auction-property/`, {
        method: "POST",
        body: formPayload,
      });

      if (response.ok) {
        const result = await response.json();
        console.log("✅ Auction saved successfully:", result);
        setAlertMessage("Auction saved successfully!");
        setShowSuccess(true);
      } else {
        const errorText = await response.text();
        console.error("❌ Failed to save auction:", errorText);
        setAlertMessage("Failed to save auction. Please try again.");
        setShowFailure(true);
      }
    } catch (err) {
      console.error("🚨 Error:", err);
      setAlertMessage("An error occurred while saving. Please try again.");
      setShowFailure(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate(-1);
  const handleBackClick = () => navigate(-1);

  const propertyTypeOptions = [
    'Plots',
    'Residential Building',
    'Commercial Building',
    'Land',
    'Flat',
    'House',
    'Villa'
  ];

  return (
    <>
      <SearchBar onBackClick={handleBackClick} />

      <Box sx={{
        padding: '16px',
        backgroundColor: '#f5f1eb',
        minHeight: '100vh',
        height: '100%',
        marginTop: "15%",
        paddingBottom: "80px"
      }}>
        <FilterContainer>
          {/* Basic Information */}
          <SectionTitle>Basic Information</SectionTitle>

          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <TextField
              label="Bank Name *"
              value={formData.bankName}
              onChange={(e) => { handleFormDataChange('bankName', e.target.value); setErrors(prev => ({ ...prev, bankName: '' })); }}
              variant="outlined"
              size="small"
              error={!!errors.bankName}
              helperText={errors.bankName || ''}
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }} size="small" error={!!errors.propertyType}>
            <InputLabel>Property Type *</InputLabel>
            <Select
              value={formData.propertyType}
              label="Property Type *"
              onChange={(e) => { handleFormDataChange('propertyType', e.target.value); setErrors(prev => ({ ...prev, propertyType: '' })); }}
            >
              {propertyTypeOptions.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
            {errors.propertyType && <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>{errors.propertyType}</Typography>}
          </FormControl>

          <TextField
            fullWidth
            label="Action Type"
            placeholder="Enter action type"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.actionType}
            onChange={(e) => handleFormDataChange('actionType', e.target.value)}
          />

          {/* Location Details */}
          <SectionTitle>Location Details</SectionTitle>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Location
            </Typography>

            <Autocomplete
              onLoad={(autocomplete) => {
                autocompleteRef.current = autocomplete;
              }}
              onPlaceChanged={onPlaceChanged}
            >
              <TextField
                fullWidth
                label="Search Location *"
                variant="outlined"
                value={address}
                onChange={(e) => { setAddress(e.target.value); setErrors(prev => ({ ...prev, location: '' })); }}
                error={!!errors.location}
                helperText={errors.location || ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={getCurrentLocation} edge="end">
                        <MyLocationIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Autocomplete>

            <div style={{ ...containerStyle, position: 'relative' }}>
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={location}
                zoom={15}
                options={{ gestureHandling: 'greedy' }}
                onLoad={(map) => { mapRef.current = map; }}
                onDragStart={() => setLocationConfirmed(false)}
              />

              {/* Fixed pin at center */}
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -100%)', pointerEvents: 'none', zIndex: 10,
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
                  <path fill="#ff0000" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                </svg>
              </div>

              {/* Confirm location button */}
              <button
                type="button"
                onClick={() => {
                  if (window.google && window.google.maps && mapRef.current) {
                    const center = mapRef.current.getCenter();
                    const lat = center.lat();
                    const lng = center.lng();
                    setLocation({ lat, lng });
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                      if (status === 'OK' && results[0]) {
                        const selectedAddress = results[0].formatted_address;
                        setAddress(selectedAddress);
                        setFormData(prev => ({
                          ...prev,
                          location: selectedAddress,
                          lat: lat.toString(),
                          long: lng.toString(),
                        }));
                      } else {
                        const fallback = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
                        setAddress(fallback);
                        setFormData(prev => ({
                          ...prev,
                          location: fallback,
                          lat: lat.toString(),
                          long: lng.toString(),
                        }));
                      }
                      setLocationConfirmed(true);
                    });
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: locationConfirmed ? 'green' : 'red',
                  border: 'none', borderRadius: '50%', width: '44px', height: '44px',
                  cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                  position: 'absolute', right: '10px', top: '45%',
                  transform: 'translateY(-50%)', zIndex: 10,
                  transition: 'background-color 0.3s ease',
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24">
                  <path fill="white" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              </button>
            </div>
          </Box>

          <TextField
            fullWidth
            label="City/Town"
            placeholder="e.g. Bangalore"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.cityTown}
            onChange={(e) => handleFormDataChange('cityTown', e.target.value)}
          />

          <TextField
            fullWidth
            label="Area/Town"
            placeholder="e.g., Yeswanthpura"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.areaTown}
            onChange={(e) => handleFormDataChange('areaTown', e.target.value)}
          />

          {/* ✅ Area — numeric only */}
          <TextField
            fullWidth
            label="Area"
            placeholder="e.g., 900"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.area}
            onChange={(e) => handleFormDataChange('area', e.target.value)}
            inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}
          />

          {/* Possession Details */}
          <SectionTitle>Possession Details</SectionTitle>

          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Possession Status</InputLabel>
            <Select
              value={formData.possessionStatus}
              label="Possession Status"
              onChange={(e) => handleFormDataChange('possessionStatus', e.target.value)}
            >
              <MenuItem value="Physical">Physical</MenuItem>
              <MenuItem value="Symbolic">Symbolic</MenuItem>
              <MenuItem value="Symbolic/Physical">Symbolic/Physical</MenuItem>
            </Select>
          </FormControl>

          {/* Auction Details */}
          <SectionTitle>Auction Details</SectionTitle>

          {/* ✅ Reserve Price — numeric only, required */}
          <TextField
            fullWidth
            label="Reserve Price *"
            placeholder="e.g., 3132000"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.reservePrice}
            onChange={(e) => { handleFormDataChange('reservePrice', e.target.value); setErrors(prev => ({ ...prev, reservePrice: '' })); }}
            inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}
            error={!!errors.reservePrice}
            helperText={errors.reservePrice || ''}
          />

          {/* ✅ EMD Amount — numeric only, required */}
          <TextField
            fullWidth
            label="EMD Amount *"
            placeholder="e.g., 313200"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.emdAmount}
            onChange={(e) => { handleFormDataChange('emdAmount', e.target.value); setErrors(prev => ({ ...prev, emdAmount: '' })); }}
            inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}
            error={!!errors.emdAmount}
            helperText={errors.emdAmount || ''}
          />

          {/* ✅ Bid Increment — numeric only */}
          <TextField
            fullWidth
            label="Bid Increment"
            placeholder="e.g., 10000"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.bidIncrement}
            onChange={(e) => handleFormDataChange('bidIncrement', e.target.value)}
            inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}
          />

          <TextField
            fullWidth
            label="EMD Submission Date & Time"
            type="datetime-local"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            value={formData.emdSubmission}
            onChange={(e) => handleFormDataChange('emdSubmission', e.target.value)}
          />

          <TextField
            fullWidth
            label="Auction Start Date & Time *"
            type="datetime-local"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            value={formData.auctionStartDate}
            onChange={(e) => { handleFormDataChange('auctionStartDate', e.target.value); setErrors(prev => ({ ...prev, auctionStartDate: '' })); }}
            error={!!errors.auctionStartDate}
            helperText={errors.auctionStartDate || ''}
          />

          <TextField
            fullWidth
            label="Auction End Date & Time *"
            type="datetime-local"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            value={formData.auctionEndDate}
            onChange={(e) => { handleFormDataChange('auctionEndDate', e.target.value); setErrors(prev => ({ ...prev, auctionEndDate: '' })); }}
            error={!!errors.auctionEndDate}
            helperText={errors.auctionEndDate || ''}
          />

          {/* Contact & Files */}
          <SectionTitle>Contact & Documents</SectionTitle>

          {/* Bank Contact — digits only, required */}
          <TextField
            fullWidth
            label="Bank Contact Details *"
            placeholder="e.g., 9876543210"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.bankContact}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              handleFormDataChange("bankContact", value);
              setErrors(prev => ({ ...prev, bankContact: '' }));
            }}
            type="tel"
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: 10,
            }}
            error={!!errors.bankContact}
            helperText={errors.bankContact || ''}
          />

          <>
            {/* Full Screen Loader */}
            <FullScreenLoader open={loading} text="Saving..." />

            <Stack direction="row" spacing={1} sx={{ mt: 3, mb: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleCancel}
                disabled={loading}
                sx={{
                  borderRadius: '8px',
                  borderColor: '#8B4513',
                  color: '#8B4513',
                  '&:hover': {
                    borderColor: '#654321',
                    backgroundColor: 'rgba(139, 69, 19, 0.04)'
                  }
                }}
              >
                Cancel
              </Button>

              <StyledButton
                fullWidth
                onClick={handleSave}
                disabled={loading || isGuest || (uploadStatus && !uploadStatus.canUpload)}
              >
                {uploadStatus && !uploadStatus.canUpload
                  ? "Upload Limit Reached"
                  : isGuest
                  ? "Login to Save"
                  : "Save"}
              </StyledButton>
            </Stack>
          </>
        </FilterContainer>
      </Box>

      <SubscriptionModal
        open={showStatusModal}
        type={uploadStatus?.reason === "NO_SUBSCRIPTION" ? "NO_SUBSCRIPTION" : "LIMIT_EXCEEDED"}
        onClose={() => setShowStatusModal(false)}
      />

      <GuestLoginModal
        open={showGuestModal}
        onClose={() => setShowGuestModal(false)}
      />

      {showSuccess && (
        <SuccessAlert
          message={alertMessage}
          onClose={() => {
            setShowSuccess(false);
            navigate("/dashboard");
          }}
        />
      )}

      {showFailure && (
        <FailureAlert
          message={alertMessage}
          onClose={() => {
            console.log("Closing failure alert");
            setShowFailure(false);
            setAlertMessage("");
            navigate("/post-bank-properties");
          }}
        />
      )}

      <FormsBottomNavbar />
    </>
  );
};

export default BankAuction;